/**
 * Custom field definitions for Scratch blocks
 * This file contains custom field types that extend ScratchBlocks fields
 */

import CSP from "../api/csp";

/**
 * Simple inheritance helper function, similar to goog.inherits from the Closure library
 * @param {Function} childCtor - Child constructor
 * @param {Function} parentCtor - Parent constructor
 */
const inherits = function (childCtor, parentCtor) {
    childCtor.superClass_ = parentCtor.prototype;
    childCtor.prototype = Object.create(parentCtor.prototype);
    childCtor.prototype.constructor = childCtor;
};

/**
 * Initialize custom fields for ScratchBlocks
 * @param {Object} ScratchBlocks - The ScratchBlocks object
 * @param {Function} intlCallback - Function to get internationalized messages
 */
export default function initCustomFields(ScratchBlocks, intlCallback) {
    /**
     * Custom field for image upload to OSS
     * Allows users to upload images (max 30KB) and stores them as OSS URLs
     * @param {string} opt_value - Optional initial value (image URL)
     * @constructor
     * @extends {ScratchBlocks.FieldTextInput}
     */
    ScratchBlocks.FieldImageUpload = function (opt_value) {
        this.imageUrl_ = opt_value || "";
        this.fileName_ = "";
        this.fileSize_ = 0;

        // Call parent constructor with initial display text
        const displayText = this.imageUrl_ ? 
            (intlCallback ? intlCallback("已上传") : "已上传") : 
            (intlCallback ? intlCallback("上传图片") : "上传图片");
        ScratchBlocks.FieldImageUpload.superClass_.constructor.call(
            this,
            displayText
        );
        this.addArgType("string");
    };

    // Set up inheritance from FieldTextInput
    inherits(ScratchBlocks.FieldImageUpload, ScratchBlocks.FieldTextInput);

    /**
     * Construct a FieldImageUpload from a JSON arg object
     * @param {!Object} options - A JSON object with options
     * @returns {!ScratchBlocks.FieldImageUpload} The new field instance
     * @package
     * @nocollapse
     */
    ScratchBlocks.FieldImageUpload.fromJson = function (options) {
        const value = options.value || options.text || "";
        return new ScratchBlocks.FieldImageUpload(value);
    };

    // Field properties
    ScratchBlocks.FieldImageUpload.prototype.CURSOR = "pointer";
    ScratchBlocks.FieldImageUpload.prototype.SERIALIZABLE = true;

    /**
     * Get the value of this field (image URL)
     * @return {string} The image URL
     * @override
     */
    ScratchBlocks.FieldImageUpload.prototype.getValue = function () {
        return this.imageUrl_ || "";
    };

    /**
     * Set the value of this field
     * @param {string} newValue - The new image URL
     * @param {string} fileName - Optional filename
     * @param {number} fileSize - Optional file size in bytes
     * @override
     */
    ScratchBlocks.FieldImageUpload.prototype.setValue = function (
        newValue,
        fileName,
        fileSize
    ) {
        if (newValue === null) {
            return;
        }

        // Check if this is a valid URL or empty string
        const isImageUrl =
            !newValue ||
            newValue.startsWith("http://") ||
            newValue.startsWith("https://") ||
            newValue.startsWith("/");

        if (isImageUrl) {
            if (newValue === this.imageUrl_) {
                return; // No change
            }
            this.imageUrl_ = newValue;

            if (fileName) {
                this.fileName_ = fileName;
            }
            if (fileSize !== undefined) {
                this.fileSize_ = fileSize;
            }
        }

        // Update display text based on current imageUrl_
        this.updateText_();
    };

    /**
     * Update the display text based on current state
     * @private
     */
    ScratchBlocks.FieldImageUpload.prototype.updateText_ = function () {
        let displayText;
        if (this.imageUrl_ && this.fileName_ && this.fileSize_) {
            const sizeKB = (this.fileSize_ / 1024).toFixed(1);
            displayText = `${this.fileName_} (${sizeKB}KB)`;
        } else if (this.imageUrl_) {
            displayText = intlCallback ? intlCallback("已上传") : "已上传";
        } else {
            displayText = intlCallback ? intlCallback("上传图片") : "上传图片";
        }

        // Call parent's setText to update display without triggering setValue
        ScratchBlocks.FieldImageUpload.superClass_.setText.call(
            this,
            displayText
        );
    };

    /**
     * Show the editor for this field (file picker)
     * @override
     * @private
     */
    ScratchBlocks.FieldImageUpload.prototype.showEditor_ = function () {
        // Create hidden file input element
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.style.display = "none";
    
        const self = this;
    
        // Handle file selection
        input.addEventListener("change", async function (e) {
            const file = e.target.files[0];
            if (!file) {
                return;
            }
    
            // Validate file type
            if (!file.type.startsWith("image/")) {
                alert(intlCallback ? intlCallback("请选择有效的图片文件") : "请选择有效的图片文件");
                return;
            }
    
            // Check file size (30KB = 30720 bytes)
            const maxSize = 500 * 1024; // 500KB
            if (file.size > maxSize) {
                const fileSizeKB = (file.size / 1024).toFixed(1);
                alert(
                    intlCallback 
                        ? intlCallback(`图片大小不能超过 500kb\n当前大小：${fileSizeKB}KB\n请选择更小的图片`)
                        : `图片大小不能超过 500kb\n当前大小：${fileSizeKB}KB\n请选择更小的图片`
                );
                return;
            }
    
            // Store the old value for the event
            const oldValue = self.imageUrl_;
    
            try {
                const response = (await CSP.uploadImage(file)).data;
    
                if (response && response.code === 200 && response.data) {
                    const imageUrl = response.data.url;
                    // const fileName = response.data.fileName || file.name;
    
                    // Set the new value with filename and size
                    self.setValue(imageUrl, file.name, file.size);
    
                    // Trigger a change event so the block knows it's been modified
                    if (self.sourceBlock_ && ScratchBlocks.Events.isEnabled()) {
                        ScratchBlocks.Events.fire(
                            new ScratchBlocks.Events.BlockChange(
                                self.sourceBlock_,
                                "field",
                                self.name,
                                oldValue,
                                imageUrl
                            )
                        );
                    }
                } else {
                    console.error("Image upload failed:", response);
                    const errorMsg =
                        response?.msg || response?.message || (intlCallback ? intlCallback("上传失败") : "上传失败");
                    alert(intlCallback ? intlCallback(`图片上传失败：${errorMsg}`) : `图片上传失败：${errorMsg}`);
                }
            } catch (error) {
                console.error("Image upload error:", error);
                alert(intlCallback ? intlCallback("图片上传失败，请重试") : "图片上传失败，请重试");
            }
        });
    
        // Trigger file picker
        input.click();
    };

    // Register the custom field type
    ScratchBlocks.Field.register(
        "field_image_upload",
        ScratchBlocks.FieldImageUpload
    );
}
